import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getHomeCarouselBrands, getBrands } from '../../lib/api';
import { dedupeBrandsBySlug } from '../../lib/utils';

export const BrandLogoCarousel = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const touchStartX = useRef(0);
  const touchScrollStart = useRef(0);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        // First try to get carousel brands with logos
        const carouselRes = await getHomeCarouselBrands();
        const carouselBrands = dedupeBrandsBySlug(carouselRes.data || []);
        const carouselWithLogos = carouselBrands.filter(b => b.logo && b.show_on_home);

        if (carouselWithLogos.length >= 3) {
          setBrands(carouselBrands);
        } else {
          const allBrandsRes = await getBrands();
          setBrands(dedupeBrandsBySlug(allBrandsRes.data).slice(0, 20));
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  // Auto-scroll animation
  const animate = useCallback(() => {
    if (!scrollRef.current || isPaused) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    scrollPositionRef.current += 0.5; // Smooth scroll speed
    const container = scrollRef.current;
    const maxScroll = container.scrollWidth / 2;

    if (scrollPositionRef.current >= maxScroll) {
      scrollPositionRef.current = 0;
    }

    container.scrollLeft = scrollPositionRef.current;
    animationRef.current = requestAnimationFrame(animate);
  }, [isPaused]);

  useEffect(() => {
    if (brands.length > 0) {
      animationRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [brands.length, animate]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setIsPaused(true);
    touchStartX.current = e.touches[0].clientX;
    touchScrollStart.current = scrollRef.current?.scrollLeft || 0;
  };

  const handleTouchMove = (e) => {
    if (!scrollRef.current) return;
    const touchX = e.touches[0].clientX;
    const diff = touchStartX.current - touchX;
    scrollRef.current.scrollLeft = touchScrollStart.current + diff;
    scrollPositionRef.current = scrollRef.current.scrollLeft;
  };

  const handleTouchEnd = () => {
    setTimeout(() => setIsPaused(false), 1000);
  };

  if (loading) {
    return (
      <section className="py-16 bg-[#0B1F3B]" data-testid="brand-carousel-loading">
        <div className="container-custom">
          <div className="text-center">
            <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs">
              Trusted Wholesaler
            </span>
            <h3 className="text-white heading-md mt-2 mb-8">Our Premium Brands</h3>
            <div className="animate-pulse flex justify-center gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="w-24 h-12 bg-white/10 rounded" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (brands.length === 0) {
    return null; // Don't render section if no brands
  }

  const uniqueBrands = dedupeBrandsBySlug(brands);
  const carouselBrandsWithLogos = uniqueBrands.filter(b => b.logo && b.show_on_home);
  const isLogoMode = carouselBrandsWithLogos.length >= 3;

  const brandsToShow = isLogoMode ? carouselBrandsWithLogos : uniqueBrands;

  // Duplicate brands for seamless infinite loop
  const displayBrands = [...brandsToShow, ...brandsToShow];

  return (
    <section className="py-16 bg-[#0B1F3B] overflow-hidden" data-testid="brand-carousel-section">
      <div className="container-custom">
        <div className="text-center mb-10">
          <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs">
            Trusted Wholesaler
          </span>
          <h3 className="text-white heading-md mt-2">Our Premium Brands</h3>
        </div>
      </div>

      {/* Carousel Container */}
      <div
        ref={scrollRef}
        className="flex gap-8 md:gap-12 lg:gap-16 overflow-hidden cursor-grab active:cursor-grabbing px-8"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ scrollBehavior: 'auto' }}
        data-testid="brand-carousel"
      >
        {displayBrands.map((brand, index) => (
          <Link
            key={`${brand.id}-${index}`}
            to={`/products?brand=${brand.slug}`}
            className="flex-shrink-0 flex items-center justify-center px-4 py-2 transition-all duration-300 hover:scale-110 group"
            data-testid={`carousel-brand-${brand.slug}`}
          >
            {isLogoMode && brand.logo ? (
              <img
                src={brand.logo}
                alt={brand.name}
                className="h-10 md:h-12 lg:h-14 w-auto max-w-[120px] md:max-w-[150px] object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <span className="text-white/70 group-hover:text-[#D4AF37] transition-colors text-sm md:text-base uppercase tracking-[0.15em] font-medium whitespace-nowrap">
                {brand.name}
              </span>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
};

export default BrandLogoCarousel;
